import { pathFromSteps, type ArrowShape } from '../model/arrow';
import { createActiveMask, normalizeBoard, type BoardData } from '../model/board';
import type { Direction } from '../model/direction';

/**
 * Hand-authored tutorial levels.
 *
 * These are the only levels not produced by the generator. They use the same BoardData
 * model and are held to the same validation and solvability checks (see
 * tests/unit/generation.test.ts).
 *
 * Each arrow is written as a start cell plus a string of steps from tail to head:
 * `U`, `R`, `D`, `L`. The head points along the final step, so `{ start: [2, 5], steps:
 * 'UUR' }` is an arrow that runs up two cells, turns right, and leaves to the right.
 * Every arrow is at least two cells long, matching what the generator produces.
 */
export interface TutorialArrow {
  readonly start: readonly [number, number];
  readonly steps: string;
  /** Required only for single-cell arrows, where there is no final step to read. */
  readonly direction?: Direction;
}

export interface TutorialLevel {
  readonly levelNumber: number;
  readonly title: string;
  /** Short coaching line shown once, when the level opens. */
  readonly lesson: string;
  readonly width: number;
  readonly height: number;
  readonly arrows: readonly TutorialArrow[];
}

export const TUTORIAL_LEVELS: readonly TutorialLevel[] = [
  {
    levelNumber: 1,
    title: 'First moves',
    lesson: 'Tap an arrow to send it off the board the way it points.',
    width: 6,
    height: 8,
    arrows: [
      // Three short arrows, each with a completely clear run to an edge.
      { start: [2, 2], steps: 'U' },
      { start: [1, 5], steps: 'L' },
      { start: [4, 6], steps: 'R' },
    ],
  },
  {
    levelNumber: 2,
    title: 'Long arrows',
    lesson: 'An arrow can wind across the board. The head is the end that leaves first.',
    width: 7,
    height: 9,
    arrows: [
      // The tail follows the head around every corner, so shape never blocks a move.
      { start: [4, 1], steps: 'LLU' },
      { start: [2, 5], steps: 'RRD' },
      { start: [5, 7], steps: 'UUR' },
    ],
  },
  {
    levelNumber: 3,
    title: 'Blocked',
    lesson: 'An arrow in the way blocks the exit. Clear the outer one first.',
    width: 7,
    height: 9,
    arrows: [
      // The upper arrow points down into the lower one: it cannot move until that leaves.
      { start: [1, 1], steps: 'RRD' },
      { start: [3, 6], steps: 'RRD' },
      { start: [5, 3], steps: 'UR' },
    ],
  },
  {
    levelNumber: 4,
    title: 'Costly taps',
    lesson: 'Tapping a blocked arrow costs a heart. You have three.',
    width: 8,
    height: 10,
    arrows: [
      // A three-deep chain in one column, cleared from the bottom up.
      { start: [1, 1], steps: 'RRD' },
      { start: [1, 4], steps: 'RRD' },
      { start: [1, 7], steps: 'RRD' },
      { start: [6, 2], steps: 'DDL' },
      { start: [5, 8], steps: 'R' },
    ],
  },
  {
    levelNumber: 5,
    title: 'Clear the board',
    lesson: 'Remove every arrow to finish. Drag to pan and pinch to zoom on bigger boards.',
    width: 8,
    height: 10,
    arrows: [
      { start: [0, 0], steps: 'DDR' },
      { start: [4, 1], steps: 'RRU' },
      { start: [2, 3], steps: 'DDL' },
      { start: [6, 4], steps: 'DDR' },
      { start: [1, 6], steps: 'RRD' },
      { start: [4, 8], steps: 'RU' },
      { start: [7, 8], steps: 'D' },
    ],
  },
];

function toShape(arrow: TutorialArrow): ArrowShape {
  return pathFromSteps(arrow.start[0], arrow.start[1], arrow.steps, arrow.direction ?? 'up');
}

export function tutorialBoard(level: TutorialLevel): BoardData {
  const activeMask = createActiveMask(level.width, level.height, 1);
  return normalizeBoard(level.width, level.height, activeMask, level.arrows.map(toShape));
}

export function getTutorialLevel(levelNumber: number): TutorialLevel | undefined {
  return TUTORIAL_LEVELS.find((level) => level.levelNumber === levelNumber);
}
